import React from 'react';
import PropTypes from 'prop-types';

import dayjs from 'dayjs';
import { css, StyleSheet } from 'aphrodite';
import { faBuilding } from '@fortawesome/free-solid-svg-icons';
import { Table } from 'react-bootstrap';

import {
  backgroundGrey,
  backgroundGrey5,
  coolGrey,
  grape,
  greyStroke,
  softGreen,
  softGreenLight,
  white,
} from 'lib/css/colors';
import Container from 'components/shared/Container';
import CopyableText from 'components/shared/CopyableText';
import { DATE_FORMAT_WITH_TIME } from 'lib/constants';
import EmptyState from 'components/shared/EmptyState';
import placeholderProductImage from 'images/placeholder-img-product-v2.svg';

function ManageVendorsTable({ vendors }) {
  return (
    <Container>
      <Table className={css(styles.tableText)}>
        <thead className={css(styles.thead)}>
          <tr>
            <th className={css(styles.logoHeader)}>Logo</th>
            <th>Distributor Name</th>
            <th>FSA ID</th>
            <th>CND ID</th>
            <th># of Products</th>
            <th>DP Partner</th>
            <th>Last Modified</th>
            <th>Created</th>
          </tr>
        </thead>
        <tbody className={css(styles.tbody)}>
          {(!vendors || vendors.length === 0) && (
            <tr>
              <td colSpan={8}>
                <EmptyState
                  icon={faBuilding}
                  title="No distributors found"
                  description={
                    'Try adjusting your search or filter criteria ' +
                    "to find what you're looking for."
                  }
                />
              </td>
            </tr>
          )}
          {vendors?.map((vendor, index) => (
            <tr
              key={index}
              className={css(styles.clickableRow)}
              onClick={() => window.open(`/enrich-products/vendor-details/${vendor.id}`, '_blank')}
            >
              <td className={css(styles.logoCell)}>
                <img
                  src={vendor.logo_url || placeholderProductImage}
                  alt={`${vendor.name} logo`}
                  className={css(styles.vendorLogo)}
                  onError={e => {
                    e.target.onerror = null;
                    e.target.src = placeholderProductImage;
                  }}
                />
              </td>
              <td>
                <CopyableText text={vendor.name} tooltipText="Copy name" />
              </td>
              <td>
                <CopyableText text={String(vendor.id)} tooltipText="Copy FSA ID" />
              </td>
              <td>
                {vendor.cnd_id ? (
                  <CopyableText text={String(vendor.cnd_id)} tooltipText="Copy CND ID" />
                ) : (
                  ''
                )}
              </td>
              <td>{vendor.product_count?.toLocaleString() ?? '-'}</td>
              <td>
                <span
                  className={css(
                    styles.badge,
                    vendor.is_dp_partner ? styles.badgeYes : styles.badgeNo,
                  )}
                >
                  {vendor.is_dp_partner ? 'Yes' : 'No'}
                </span>
              </td>
              <td>{dayjs(vendor.date_modified).format(DATE_FORMAT_WITH_TIME)}</td>
              <td>{dayjs(vendor.date_created).format(DATE_FORMAT_WITH_TIME)}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Container>
  );
}

const styles = StyleSheet.create({
  tableText: {
    fontSize: '0.95rem',
  },
  thead: {
    position: 'sticky',
    top: 0,
    zIndex: 1,
    backgroundColor: backgroundGrey,
  },
  tbody: {
    zIndex: 0,
    position: 'relative',
  },
  logoHeader: {
    width: 70,
    textAlign: 'center',
  },
  logoCell: {
    width: 70,
    padding: '4px 8px',
  },
  vendorLogo: {
    width: 48,
    height: 48,
    objectFit: 'contain',
    borderRadius: 6,
    border: `1px solid ${greyStroke}`,
    backgroundColor: white,
  },
  clickableRow: {
    cursor: 'pointer',
    ':hover': {
      backgroundColor: `${grape}10`,
    },
  },
  badge: {
    padding: '4px 10px',
    borderRadius: 12,
    fontSize: 12,
    fontWeight: 600,
    display: 'inline-block',
  },
  badgeYes: {
    backgroundColor: softGreenLight,
    color: softGreen,
  },
  badgeNo: {
    backgroundColor: backgroundGrey5,
    color: coolGrey,
  },
});

ManageVendorsTable.propTypes = {
  vendors: PropTypes.array,
};

export default ManageVendorsTable;
